import { useCallback, useMemo, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { FIELD_TYPES } from 'utility/constants';

import { fieldsFilter, getResolutionOptions } from 'hooks/useChangeField';
import { useAutomationGetFieldOptions } from './useAutomationGetFieldOptions';

import { addOnlyPedicate, fieldTypeFilter, hasOptionsWizard } from '../common';
import { isPrimaryField } from 'components/Modals/utilities';
import { newFieldValueMappings } from '../../CommonComponents/valueMappingsCommon';
import { cfvChangeTypeOptionsConst } from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/ChangeFieldValue/common';
import {
  Field,
  GenericOption,
  VariableOption,
} from 'pages/AutomationEngine/types';

type FieldType = {
  type?: string;
  label?: string;
  localizedLabel?: (t: TFunction) => string;
  icon?: any;
  placeholder?: string;
  localizedPlaceholder?: (t: TFunction) => string;
};

type LabelWithOptions = Array<{ label: string; options: Array<any> }>;

type ChangeTypeOption = GenericOption & {
  filterable: boolean;
  isVariable: boolean;
};

type OptionWithFieldData = GenericOption & {
  fieldData: Field;
  options: Array<any>;
};

const setChangeType = (
  changeTypeOptions: Array<ChangeTypeOption>,
  changeType: ChangeTypeOption
) => {
  if (!changeType) {
    return changeTypeOptions[0];
  }
  return (
    changeTypeOptions.filter((opt) => opt.value === changeType.value)?.[0] ||
    changeTypeOptions[0]
  );
};

export const automationTargetRelationshipOptionsAllowedCardinality = [
  'many_to_one',
  'one_to_one',
];

type UseAutomationChangeField = {
  objectId: any;
  model: any;
  values: any;
  onChange: any;
  changeTypeOptions: Array<ChangeTypeOption>;
  automationContextFields: any;
  automationContextCategories: any;
  handleClearAllError: any;
};

export const useAutomationChangeField = ({
  objectId,
  model,
  values,
  onChange,
  changeTypeOptions,
  automationContextFields,
  automationContextCategories,
  handleClearAllError,
}: UseAutomationChangeField) => {
  const { t } = useTranslation();
  const [entityModel] = useState(model);

  const {
    fieldToModify: _fieldToModify,
    specificFieldValue,
    changeType: _changeType,
    relatedObject,
    automationTargetRelationshipField,
    relatedObjectField,
    fieldResolution,
    thisRecordField,
    variable,
  } = values;

  const changeType = setChangeType(changeTypeOptions, _changeType);

  const [resolutionOptions, setResolutionOptions] = useState(() =>
    getResolutionOptions(t, addOnlyPedicate(_fieldToModify))
  );

  // eventually we can just return the options
  const {
    options: unfilteredOptions,
    relatedOptions,
  }: {
    options: Array<OptionWithFieldData>;
    relatedOptions: Array<OptionWithFieldData>;
  } = useAutomationGetFieldOptions({
    objectId,
    defaultFieldsFilter: fieldsFilter,
    automationContextFields,
    automationContextCategories,
    fieldToModify: _fieldToModify,
  });
  // but for now we need to filter them

  const options = useMemo(
    () =>
      unfilteredOptions?.reduce<LabelWithOptions>((acc, { label, options }) => {
        const opts = options.filter(({ fieldData }) =>
          fieldTypeFilter(fieldData)
        );
        return opts.length ? [...acc, { label, options: opts }] : acc;
      }, []),
    [unfilteredOptions]
  );

  //as now we can have multiple primary related fields to an automation context object, we need this options to select one
  const automationTargetRelationshipOptions = useMemo(() => {
    return options?.reduce<LabelWithOptions>(
      (acc, { label, options: allOptions }) => {
        const opts = allOptions.filter(
          ({ fieldData }) =>
            fieldData?.fieldType === FIELD_TYPES.Relationship.type &&
            fieldData?.relation?.relatedObject === relatedObject?.value &&
            automationTargetRelationshipOptionsAllowedCardinality.includes(
              fieldData?.relation?.cardinality
            )
        );
        return opts.length ? [...acc, { label, options: opts }] : acc;
      },
      []
    );
  }, [options, relatedObject?.value]);

  // because the options don't always come back with the data in saved automations
  const { fieldToModify } = useMemo(() => {
    // build a dictionary
    const idToOptions = (unfilteredOptions || [])
      .flatMap((cat) => cat.options)
      .reduce((acc, opt) => {
        return opt
          ? { ...acc, [opt.fieldData.id]: opt.fieldData.options }
          : acc;
      }, {});

    // add the looked up options
    const fieldToModify: Field & { options: Array<string>; deleted: boolean } =
      _fieldToModify
        ? {
            ..._fieldToModify,
            options: idToOptions[_fieldToModify?.id] || [],
          }
        : _fieldToModify;

    return { idToOptions, fieldToModify };
  }, [unfilteredOptions, _fieldToModify]);

  const setField = (val: any) => {
    handleClearAllError(values.key);
    const nextResolutionOptions = getResolutionOptions(t, addOnlyPedicate(val));
    setResolutionOptions(nextResolutionOptions);
    onChange({
      ...values,
      fieldToModify: val,
      specificFieldValue: { value: '' },
      tagsToRemove: null,
      automationTargetRelationshipField: null,
      relatedObjectField: null,
      changeType: changeTypeOptions[0],
      fieldResolution: nextResolutionOptions[0],
      thisRecordField: null,
      variable: null,
    });
  };

  const setFieldValue = useCallback(
    (val: any) => {
      const { specificFieldValue } = values;
      onChange({
        ...values,
        specificFieldValue: { ...specificFieldValue, ...val },
      });
    },
    [values, onChange]
  );

  const handleChangeRemoveTags = useCallback(
    (val: any, whichTags: string) => {
      const { specificFieldValue } = values;
      onChange({
        ...values,
        specificFieldValue: {
          value: null,
          ...specificFieldValue,
          [whichTags]: [...val],
        },
      });
    },
    [values, onChange]
  );

  const handleChangeFieldValue = useCallback(
    (data: any) => {
      let option = fieldToModify.options.find(({ id }) => id === data);
      switch (fieldToModify.fieldType) {
        case FIELD_TYPES.Dropdown.type:
        case FIELD_TYPES.Radio.type:
        case FIELD_TYPES.Status.type: {
          option = fieldToModify.options.find(({ id }) => id === data);
          break;
        }
        case FIELD_TYPES.Checkboxes.type: {
          option = data.map((item: string) =>
            fieldToModify.options.find(({ id }) => id === item)
          );
          break;
        }
        default: {
          option = data;
        }
      }
      // wrap the value in an object to force re render if validation keeps the number the same...
      setFieldValue({
        value: option,
      });
      return option;
    },
    [fieldToModify, setFieldValue]
  );

  const handleChangeType = useCallback(
    (changeType: any) => {
      handleClearAllError(values.key);

      onChange({
        ...values,
        changeType,
        specificFieldValue:
          changeType.value === cfvChangeTypeOptionsConst.SPECIFIC_VALUE
            ? {}
            : null,
        relatedObject: null,
        automationTargetRelationshipField: null,
        relatedObjectField: null,
        fieldResolution: resolutionOptions[0],
        fieldValueMappings: [],
        thisRecordField: null,
        variable: null,
      });
    },
    [handleClearAllError, values, onChange, resolutionOptions]
  );

  const handleChangeRelatedObject = useCallback(
    (relatedObject: any) => {
      handleClearAllError(values.key);
      onChange({
        ...values,
        relatedObject,
        automationTargetRelationshipField: null,
        relatedObjectField: null,
        fieldResolution: resolutionOptions[0],
        fieldValueMappings: [],
        thisRecordField: null,
      });
    },
    [handleClearAllError, values, onChange, resolutionOptions]
  );

  const handleChangeAutomationTargetRelationshipField = useCallback(
    (automationTargetRelationshipField: any) => {
      handleClearAllError(values.key, 'automationTargetRelationshipField');
      onChange({
        ...values,
        automationTargetRelationshipField,
      });
    },
    [handleClearAllError, values, onChange]
  );

  const handleChangePrimaryRelatedObjectField = useCallback(
    (relatedObjectField: any) => {
      handleClearAllError(values.key, 'relatedObjectField');
      onChange({
        ...values,
        relatedObjectField,
        fieldResolution: resolutionOptions[0],
        fieldValueMappings: [newFieldValueMappings(0)],
      });
    },
    [handleClearAllError, values, onChange, resolutionOptions]
  );

  const handleChangeFieldResolution = useCallback(
    (fieldResolution: any) => {
      onChange({
        ...values,
        fieldResolution,
      });
    },
    [values, onChange]
  );

  const handleChangeThisRecordField = useCallback(
    (thisRecordField: any) => {
      handleClearAllError(values.key, 'relatedObjectField');
      onChange({
        ...values,
        relatedObject: {
          id: model?.id,
          value: model?.id,
          label: model?.objectName,
        },
        relatedObjectField: null,
        fieldResolution: resolutionOptions[0],
        fieldValueMappings: [newFieldValueMappings(0)],
        thisRecordField,
      });
    },
    [
      handleClearAllError,
      values,
      onChange,
      model?.id,
      model?.objectName,
      resolutionOptions,
    ]
  );

  const handleChangeVariable = useCallback(
    (variable: VariableOption) => {
      onChange({
        ...values,
        variable,
        ...(values?.changeType?.value ===
        cfvChangeTypeOptionsConst.ENTITY_VARIABLE_FIELD
          ? {
              relatedObjectField: null,
              fieldResolution: resolutionOptions[0],
              fieldValueMappings: [],
              thisRecordField: null,
            }
          : null),
      });
    },
    [onChange, values, resolutionOptions]
  );

  const getPlaceholder = useMemo(() => {
    if (fieldToModify) {
      if (fieldToModify.fieldType === FIELD_TYPES.Relationship.type) {
        let placeholder = t('Record');
        const isClientURL = fieldToModify?.relation?.fetchUrl === 'client';
        if (isPrimaryField(fieldToModify)) {
          if (isClientURL) {
            placeholder = t('Contact');
          }
        } else {
          placeholder = t('Records');
          if (isClientURL) {
            placeholder = t('Contacts');
          }
        }
        return `${t('Find')} ${placeholder}`;
      }

      const typeDictionary = Object.values<FieldType>(FIELD_TYPES).reduce(
        (acc, { type, placeholder, localizedPlaceholder }) => {
          const value = localizedPlaceholder || placeholder;
          if (value) {
            acc = {
              ...acc,
              [type!]: {
                placeholder: typeof value === 'function' ? value(t) : value,
              },
            };
          }
          return acc;
        },
        {} as Record<string, { placeholder: string }>
      );

      if (typeDictionary[fieldToModify.fieldType]) {
        return typeDictionary[fieldToModify.fieldType].placeholder;
      }
      return fieldToModify.displayName;
    }
    return undefined;
  }, [fieldToModify, t]);

  const isDynamicTagType =
    fieldToModify && fieldToModify.fieldType === FIELD_TYPES.DynamicTags.type;

  const showOptionWizard = hasOptionsWizard(
    fieldToModify,
    changeType,
    relatedObjectField
  );

  const showOptionsWizardForFieldsOnRecord = hasOptionsWizard(
    fieldToModify,
    changeType,
    thisRecordField
  );

  const showOptionsWizardForVariable = !!hasOptionsWizard(
    fieldToModify,
    changeType,
    variable?.meta?.dataSubtype?.value
  );

  return {
    entityModel,
    fieldToModify,
    specificFieldValue,
    changeType,
    relatedObject,
    automationTargetRelationshipField,
    relatedObjectField,
    fieldResolution,
    thisRecordField,
    variable,

    options,
    automationTargetRelationshipOptions,
    relatedOptions,
    showOptionWizard,
    showOptionsWizardForFieldsOnRecord,
    showOptionsWizardForVariable,

    setField,
    setFieldValue,

    handleChangeRemoveTags,
    handleChangeFieldValue,
    handleChangeType,
    handleChangeRelatedObject,
    handleChangeAutomationTargetRelationshipField,
    handleChangePrimaryRelatedObjectField,
    handleChangeFieldResolution,
    handleChangeThisRecordField,
    handleChangeVariable,

    getPlaceholder,
    isDynamicTagType,
    isShowResolutionField: !!fieldToModify,
    resolutionOptions,
  };
};
